import { ReactNode, useMemo } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BdcBranch, BdcImportPreviewData, BdcStorePlace } from "../../../types/bdc.type";
import BranchChip from "../Branch/BranchChip";
import TestModeBanner from "../Branch/TestModeBanner";
import { baht, lineText, pcs, roundLabel, thaiDate } from "../format";
import { CARD_SX, SCROLL_SX, TABLE_SX } from "../styles";
import Tag from "../Tag";
import { blockingCount, buildBranchSplit, buildCompleteness, isConfirmBlocked, overwriteCount } from "./completeness";
import { statusLabel } from "../status";
import KpiRow from "../KpiRow";

interface PreviewSectionProps {
  preview: BdcImportPreviewData;
  applying: boolean;
  onConfirm: () => void;
  /** The BDC branches the viewer sees — chip colors and the test-mode banner. */
  branches?: BdcBranch[];
  /** A link to ตั้งค่า BDC › DC (built by the page, which owns the router). */
  dcSettingsLink?: ReactNode;
  /** Why the last confirm failed, shown by the button. */
  confirmNotice?: ReactNode;
}

const placeText = (p: BdcStorePlace) =>
  `${p.dc_code} · ${p.round == null ? "-" : `รอบ ${p.round}`} · สาย ${lineText(p.line_no)} · จุด ${lineText(p.stop_no)}`;

/** One check: a summary line that opens onto its rows. Empty checks render as a single ✓ line. */
function CheckBlock({
  title,
  count,
  tone,
  blocking,
  children,
}: Readonly<{
  title: string;
  count: number;
  tone: "crit" | "warn" | "info";
  blocking?: boolean;
  children: ReactNode;
}>) {
  if (count === 0) {
    return (
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #EEF0F3" }}>
        <Tag tone="ok">✓</Tag>{" "}
        <Typography component="span" variant="body2">
          {title} — ไม่พบ
        </Typography>
      </Box>
    );
  }
  return (
    <Accordion disableGutters elevation={0} sx={{ "&:before": { display: "none" }, borderBottom: "1px solid #EEF0F3" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Tag tone={tone}>{pcs(count)}</Tag>
          <Typography variant="body2" fontWeight={600}>
            {title}
          </Typography>
          {blocking ? <Tag tone="crit">ต้องแก้ก่อนยืนยัน</Tag> : <Tag tone="mut">แจ้งให้ทราบ</Tag>}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Box sx={{ ...SCROLL_SX, maxHeight: 360 }}>{children}</Box>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * ขั้น 2 · ตรวจ + ตัวอย่าง — what the files hold, which BDC branch each PO goes to, and what is
 * wrong with them. Mismatches, orphan PICKLST, parse errors, duplicate file names and DCs not yet
 * bound to a branch block the confirm (as does the server's `is_blocked`); unmatched SKUs and new / moved stores
 * only inform (import confirm creates and updates those masters itself).
 */
export default function PreviewSection({
  preview,
  applying,
  onConfirm,
  branches = [],
  dcSettingsLink,
  confirmNotice,
}: PreviewSectionProps) {
  const { groups, rounds } = useMemo(() => buildCompleteness(preview.completeness), [preview]);
  const split = useMemo(() => buildBranchSplit(preview.completeness), [preview]);
  const blocking = blockingCount(preview);
  const blocked = isConfirmBlocked(preview);
  const unboundDcs = preview.checks.unbound_dcs ?? [];
  const previewBranches = branches.filter((b) => split.some((row) => row.company_id === b.company_id));
  const overwrites = overwriteCount(preview.completeness);
  const { checks, errors, summary } = preview;
  const lockedPos = checks.locked_pos ?? [];
  const changedLocked = lockedPos.filter((po) => !po.is_identical);
  const multiDay = preview.order_dates.length > 1;

  return (
    <Stack gap={2} sx={TABLE_SX}>
      <KpiRow
        items={[
          { label: "PO", value: pcs(summary.po_count), sub: `วันที่สั่ง ${preview.order_dates.map(thaiDate).join(", ") || "-"}` },
          { label: "ร้าน", value: pcs(summary.store_count) },
          { label: "ชิ้น", value: pcs(summary.piece_count) },
          { label: "ยอดก่อน VAT", value: baht(summary.subtotal), sub: `VAT ${baht(summary.vat)}` },
          { label: "ยอดรวม VAT", value: `฿${baht(summary.total)}` },
        ]}
      />

      {unboundDcs.length > 0 && (
        <Alert severity="error" data-testid="bdc-unbound-dcs">
          <AlertTitle>มี DC ที่ยังไม่ผูกสาขา BDC {pcs(unboundDcs.length)} DC — นำเข้าไม่ได้จนกว่าจะผูก</AlertTitle>
          <Stack gap={1}>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {unboundDcs.map((dc) => (
                <Tag key={dc.dc_code} tone="crit">
                  {dc.dc_code} {dc.dc_name}
                  {dc.is_new && " · DC ใหม่"}
                </Tag>
              ))}
            </Stack>
            <Typography variant="body2">
              ให้หัวหน้า BDC เลือกสาขาที่จัดส่งของ DC เหล่านี้ในหน้า ตั้งค่า BDC › DC แล้วกลับมาตรวจไฟล์ชุดนี้ใหม่
            </Typography>
            {dcSettingsLink && <Box>{dcSettingsLink}</Box>}
          </Stack>
        </Alert>
      )}

      <TestModeBanner branches={previewBranches} />

      <Card sx={CARD_SX}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E3E5EA" }}>
          <Typography fontWeight={600}>ระบบแยกให้ตามสาขา</Typography>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <table className="bdc-t">
            <thead>
              <tr>
                <th>สาขา</th>
                <th>DC</th>
                <th className="n">PO</th>
                <th className="n">ร้าน</th>
                <th className="n">ชิ้น</th>
              </tr>
            </thead>
            <tbody>
              {split.map((row) => {
                const branch = branches.find((b) => b.company_id === row.company_id);
                return (
                  <tr key={row.company_id ?? "unbound"}>
                    <td>
                      <BranchChip branches={branches} companyId={row.company_id} branchCode={row.branch_code} />{" "}
                      <span className="sub">
                        {row.company_id ? branch?.branch_name ?? "" : "นำเข้าไม่ได้จนกว่าจะผูก DC"}
                      </span>
                    </td>
                    <td>{row.dc_codes.join(", ")}</td>
                    <td className="n">{pcs(row.po_count)}</td>
                    <td className="n">{pcs(row.store_count)}</td>
                    <td className="n">{pcs(row.pieces)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Card>

      <Card sx={CARD_SX}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E3E5EA", display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Typography fontWeight={600}>ความครบของไฟล์ ต่อ DC × รอบ</Typography>
          <Typography className="sub">ตัวเลข = ร้าน · ชิ้น</Typography>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <table className="bdc-t">
            <thead>
              <tr>
                {multiDay && <th>วันที่สั่ง</th>}
                <th>สาขา</th>
                <th>DC</th>
                <th>วันส่ง</th>
                {rounds.map((round) => (
                  <th key={round} className="c">
                    รอบ {round}
                  </th>
                ))}
                <th className="c">ขาดรายร้าน</th>
                <th className="c">ไม่มีรายร้าน</th>
                <th className="n">รวมชิ้น</th>
                <th>นำเข้าซ้ำ</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.key}>
                  {multiDay && <td>{thaiDate(group.order_date)}</td>}
                  <td>
                    <BranchChip branches={branches} companyId={group.company_id} branchCode={group.branch_code} />
                  </td>
                  <td>
                    <span className="dc">{group.dc_code}</span> <span className="sub">{group.dc_name}</span>
                  </td>
                  <td>{group.is_d_plus_1 ? <Tag tone="info">D+1</Tag> : "วันเดียวกัน"}</td>
                  {rounds.map((round) => {
                    const row = group.byRound.get(round);
                    return (
                      <td key={round} className="c">
                        {row ? (
                          <>
                            <Tag tone="ok">✓</Tag> {pcs(row.store_count)} · {pcs(row.pieces)}
                          </>
                        ) : (
                          <span className="sub">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="c">
                    {group.missing.length > 0 ? (
                      group.missing.map((row) => (
                        <Box key={row.po_number} component="span" sx={{ mr: 0.5 }}>
                          <Tag tone="crit">ขาด · {row.po_number}</Tag>
                        </Box>
                      ))
                    ) : (
                      <span className="sub">—</span>
                    )}
                  </td>
                  <td className="c">
                    {group.noStores.length > 0 ? (
                      group.noStores.map((row) => (
                        <Box key={row.po_number} component="span" sx={{ mr: 0.5 }}>
                          <Tag tone="warn">{pcs(row.pieces)} ชิ้น</Tag>
                        </Box>
                      ))
                    ) : (
                      <span className="sub">—</span>
                    )}
                  </td>
                  <td className="n">
                    <b>{pcs(group.pieces)}</b>
                  </td>
                  <td>
                    {group.alreadyImported > 0 && <Tag tone="warn">{group.alreadyImported} PO — จะเขียนทับ</Tag>}{" "}
                    {group.locked > 0 && <Tag tone="info">{group.locked} PO ยืนยันแล้ว — ไม่ทับ</Tag>}
                    {group.alreadyImported === 0 && group.locked === 0 && <span className="sub">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Accordion disableGutters elevation={0} sx={{ "&:before": { display: "none" }, borderTop: "1px solid #E3E5EA" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
            <Typography variant="body2">รายการ PO ทั้งหมด ({pcs(preview.completeness.length)})</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ ...SCROLL_SX, maxHeight: 400 }}>
              <table className="bdc-t">
                <thead>
                  <tr>
                    <th>เลข PO</th>
                    <th>สาขา</th>
                    <th>DC</th>
                    <th>รอบ</th>
                    <th>วันที่สั่ง</th>
                    <th>วันส่ง</th>
                    <th>PICKLST</th>
                    <th className="n">ร้าน</th>
                    <th className="n">ชิ้น</th>
                    <th>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.completeness.map((row) => (
                    <tr key={row.po_number}>
                      <td className="mono">{row.po_number}</td>
                      <td>
                        <BranchChip branches={branches} companyId={row.company_id} branchCode={row.branch_code} />
                      </td>
                      <td>
                        <span className="dc">{row.dc_code}</span> <span className="sub">{row.dc_name}</span>
                      </td>
                      <td>{row.kind === "X" ? <Tag tone="warn">{roundLabel(row.kind, row.round)}</Tag> : roundLabel(row.kind, row.round)}</td>
                      <td>{thaiDate(row.order_date)}</td>
                      <td>
                        {thaiDate(row.delivery_date)} {row.is_d_plus_1 && <Tag tone="info">D+1</Tag>}
                      </td>
                      <td>{row.has_picklist ? <Tag tone="ok">✓</Tag> : row.kind === "X" ? <span className="sub">—</span> : <Tag tone="crit">ขาด</Tag>}</td>
                      <td className="n">{row.kind === "X" ? "—" : pcs(row.store_count)}</td>
                      <td className="n">{pcs(row.pieces)}</td>
                      <td>
                        {row.is_locked ? (
                          <Tag tone="info">ยืนยันแล้ว — ไม่ทับ</Tag>
                        ) : row.already_imported ? (
                          <Tag tone="warn">นำเข้าซ้ำ — จะเขียนทับ</Tag>
                        ) : (
                          <Tag tone="mut">ใหม่</Tag>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Card>

      <Card sx={CARD_SX}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E3E5EA" }}>
          <Typography fontWeight={600}>ผลตรวจ</Typography>
        </Box>
        <CheckBlock title="DC ที่ยังไม่ผูกสาขา BDC" count={unboundDcs.length} tone="crit" blocking>
          <table className="bdc-t">
            <thead>
              <tr>
                <th>DC</th>
                <th>ชื่อ</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {unboundDcs.map((dc) => (
                <tr key={dc.dc_code}>
                  <td className="dc">{dc.dc_code}</td>
                  <td>{dc.dc_name}</td>
                  <td>
                    {dc.is_new ? <Tag tone="info">DC ใหม่จากไฟล์นี้</Tag> : <Tag tone="mut">มีในระบบ ยังไม่ผูก</Tag>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CheckBlock>
        <CheckBlock
          title="ข้อผิดพลาดของไฟล์ (อ่านไม่ได้ / ชื่อไฟล์ซ้ำ / DC ยังไม่ผูกสาขา)"
          count={errors.length}
          tone="crit"
          blocking
        >
          <table className="bdc-t">
            <thead>
              <tr>
                <th>ไฟล์</th>
                <th className="n">บรรทัด</th>
                <th>ปัญหา</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((error, i) => (
                <tr key={`${error.file}-${error.line}-${i}`}>
                  <td className="mono">{error.file}</td>
                  <td className="n">{error.line ? error.line : "-"}</td>
                  <td>{error.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CheckBlock>
        <CheckBlock title="ยอด PO (ORDS) ไม่ตรงกับผลรวมรายร้าน (PICKLST)" count={checks.mismatches.length} tone="crit" blocking>
          <table className="bdc-t">
            <thead>
              <tr>
                <th>เลข PO</th>
                <th>Barcode</th>
                <th className="n">ORDS</th>
                <th className="n">PICKLST</th>
                <th className="n">ต่าง</th>
              </tr>
            </thead>
            <tbody>
              {checks.mismatches.map((m) => (
                <tr key={`${m.po_number}-${m.barcode}`}>
                  <td className="mono">{m.po_number}</td>
                  <td className="mono">{m.barcode}</td>
                  <td className="n">{pcs(m.ords_qty)}</td>
                  <td className="n">{pcs(m.picklist_qty)}</td>
                  <td className="n">{pcs(m.picklist_qty - m.ords_qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CheckBlock>
        <CheckBlock title="PICKLST ที่ไม่มี PO ในไฟล์ ORDS" count={checks.orphan_picklist.length} tone="crit" blocking>
          <table className="bdc-t">
            <thead>
              <tr>
                <th>ไฟล์</th>
                <th>เลข PO</th>
              </tr>
            </thead>
            <tbody>
              {checks.orphan_picklist.map((o) => (
                <tr key={`${o.file}-${o.po_number}`}>
                  <td className="mono">{o.file}</td>
                  <td className="mono">{o.po_number}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CheckBlock>
        <CheckBlock
          title="PO ยืนยัน DO แล้ว — ไม่เขียนทับ (ความต่างระหว่างที่บันทึกไว้กับไฟล์)"
          count={lockedPos.length}
          tone="warn"
        >
          <Box data-testid="bdc-locked-pos">
            {lockedPos.map((po) => (
              <Box key={po.po_number} sx={{ borderBottom: "1px solid #EEF0F3" }}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1 }}>
                  <span className="mono">{po.po_number}</span>
                  <Tag tone="info">{statusLabel(po.status)}</Tag>
                  {po.is_identical ? (
                    <Tag tone="ok">ไฟล์ตรงกับที่บันทึกไว้</Tag>
                  ) : (
                    <Tag tone="warn">
                      ต่าง {pcs(po.difference_count)} จุด · บันทึกไว้ {pcs(po.stored_total)} ชิ้น · ไฟล์ {pcs(po.file_total)} ชิ้น
                    </Tag>
                  )}
                </Stack>
                {po.differences.length > 0 && (
                  <table className="bdc-t">
                    <thead>
                      <tr>
                        <th>ระดับ</th>
                        <th>ร้าน</th>
                        <th>Barcode</th>
                        <th className="n">บันทึกไว้</th>
                        <th className="n">ในไฟล์</th>
                        <th className="n">ต่าง</th>
                      </tr>
                    </thead>
                    <tbody>
                      {po.differences.map((d) => (
                        <tr key={`${d.level}-${d.store_code ?? ""}-${d.barcode}`}>
                          <td>{d.level === "po_line" ? "ยอด PO" : "รายร้าน"}</td>
                          <td className="mono">{d.store_code ?? "—"}</td>
                          <td className="mono">{d.barcode}</td>
                          <td className="n">{pcs(d.stored_qty)}</td>
                          <td className="n">{pcs(d.file_qty)}</td>
                          <td className="n">{pcs(d.file_qty - d.stored_qty)}</td>
                        </tr>
                      ))}
                      {po.difference_count > po.differences.length && (
                        <tr>
                          <td colSpan={6} className="sub">
                            … อีก {pcs(po.difference_count - po.differences.length)} จุด
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </Box>
            ))}
          </Box>
        </CheckBlock>
        <CheckBlock title="สินค้าที่ยังไม่จับคู่สินค้า GB" count={checks.unmatched_skus.length} tone="warn">
          <table className="bdc-t">
            <thead>
              <tr>
                <th>รหัส 7-11</th>
                <th>Barcode</th>
                <th>ชื่อในไฟล์</th>
              </tr>
            </thead>
            <tbody>
              {checks.unmatched_skus.map((s) => (
                <tr key={s.barcode}>
                  <td className="mono">{s.item_code}</td>
                  <td className="mono">{s.barcode}</td>
                  <td>{s.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CheckBlock>
        <CheckBlock title="ร้านใหม่ — ระบบสร้าง master ร้านให้ตอนยืนยัน" count={checks.new_stores.length} tone="info">
          <table className="bdc-t">
            <thead>
              <tr>
                <th>รหัสร้าน</th>
                <th>ชื่อร้าน</th>
                <th>DC · รอบ · สาย · จุดส่ง</th>
              </tr>
            </thead>
            <tbody>
              {checks.new_stores.map((s) => (
                <tr key={s.store_code}>
                  <td className="mono">{s.store_code}</td>
                  <td>{s.store_name}</td>
                  <td>{placeText(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CheckBlock>
        <CheckBlock title="ร้านย้าย DC / รอบ / สาย / จุดส่ง — อัปเดต master ตอนยืนยัน" count={checks.moved_stores.length} tone="info">
          <table className="bdc-t">
            <thead>
              <tr>
                <th>รหัสร้าน</th>
                <th>ชื่อร้าน</th>
                <th>เดิม</th>
                <th>ใหม่</th>
              </tr>
            </thead>
            <tbody>
              {checks.moved_stores.map((s) => (
                <tr key={s.store_code}>
                  <td className="mono">{s.store_code}</td>
                  <td>{s.store_name}</td>
                  <td className="sub">{placeText(s.from)}</td>
                  <td>
                    <b>{placeText(s.to)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CheckBlock>
      </Card>

      {overwrites > 0 && (
        <Alert severity="warning">
          มี {pcs(overwrites)} PO ที่เคยนำเข้าแล้ว (ยังเป็นร่าง) — ยืนยันแล้วระบบจะเขียนทับข้อมูลเดิมของ PO เหล่านั้นทั้งหมด
        </Alert>
      )}
      {lockedPos.length > 0 && (
        <Alert severity="warning" data-testid="bdc-locked-notice">
          มี {pcs(lockedPos.length)} PO ที่ยืนยัน DO แล้ว — ระบบจะไม่เขียนทับ
          {changedLocked.length > 0
            ? ` · ${pcs(changedLocked.length)} PO ในไฟล์ต่างจากที่บันทึกไว้ (ดูความต่างในผลตรวจ) ถ้าต้องใช้ยอดใหม่ ให้ยกเลิกยืนยัน DO แล้วนำเข้าใหม่`
            : " · ไฟล์ตรงกับที่บันทึกไว้"}
        </Alert>
      )}
      {blocking > 0 && (
        <Alert severity="error">
          พบปัญหาที่ต้องแก้ก่อน {pcs(blocking)} รายการ — แก้ไฟล์ (เลือกไฟล์ให้ครบ / ผูก DC กับสาขา) แล้วตรวจใหม่
        </Alert>
      )}
      {blocking === 0 && blocked && preview.completeness.length > 0 && (
        <Alert severity="error">
          ระบบไม่รับไฟล์ชุดนี้ (มี PICKLST ที่ไม่มี ORDS หรือมี DC ที่ยังไม่ผูกสาขา) — แก้แล้วตรวจใหม่
        </Alert>
      )}

      {confirmNotice}

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" onClick={onConfirm} loading={applying} disabled={blocked}>
          ยืนยันนำเข้า {pcs(preview.completeness.length)} PO
        </Button>
      </Stack>
    </Stack>
  );
}
